import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ConfirmModal from '@/components/common/ConfirmModal'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'
import LoadingState from '@/components/common/LoadingState'
import { createApiKey, deleteApiKey, fetchApiKeys, revokeApiKey, rotateApiKey, updateApiKeySites } from './companyDetailApi'
import type { ApiKeyRecord, CompanyController, SiteAccessMode, SubscriptionTier } from './companyDetailTypes'
import { formatLocal, graceCountdown } from './apiKeyFormat'

/**
 * A company's API keys, ported from legacy includes/company_details.html's
 * "API Keys" card and its Generate / Sites / Rotate modals and script, against
 * the same /portal/clients/api/v2/<client_id>/api-keys endpoints (unchanged).
 * Behaviour matches legacy:
 *  - columns Name, Key (prefix + dots), Created, Last Used (browser-local),
 *    Expires, Plan, Sites, Status (incl. the grace-period countdown);
 *  - actions by lifecycle_status: active -> Sites/Rotate/Revoke,
 *    grace_period -> Revoke, revoked/expired/grace_expired -> Delete;
 *  - Generate: required label, Standard/Premium plan, All or Selected sites,
 *    key revealed once with Copy and the x-api-key hint;
 *  - Rotate: grace period 0/3/7 (default)/14/30 days, new key revealed once;
 *  - Revoke/Delete confirm with legacy's own wording.
 * Rendered only for clients.api_keys.manage - the permission every one of
 * these endpoints requires (legacy gated the actions on admin/super user).
 */
export default function ApiKeysSection({ clientId, controllers }: { clientId: string; controllers: CompanyController[] }) {
  const queryClient = useQueryClient()
  const keysQueryKey = ['company', clientId, 'api-keys']
  const keysQuery = useQuery({ queryKey: keysQueryKey, queryFn: () => fetchApiKeys(clientId), enabled: Boolean(clientId) })
  const refreshKeys = () => queryClient.invalidateQueries({ queryKey: keysQueryKey })

  const [showGenerate, setShowGenerate] = useState(false)
  const [sitesKey, setSitesKey] = useState<ApiKeyRecord | null>(null)
  const [rotateKey, setRotateKey] = useState<ApiKeyRecord | null>(null)
  const [confirm, setConfirm] = useState<{ kind: 'revoke' | 'delete'; key: ApiKeyRecord } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(clientId, keyId),
    onSuccess: () => {
      setConfirm(null)
      refreshKeys()
    },
    onError: (err) => {
      setConfirm(null)
      setActionError(`Failed to revoke key: ${errorText(err)}`)
    },
  })
  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteApiKey(clientId, keyId),
    onSuccess: () => {
      setConfirm(null)
      refreshKeys()
    },
    onError: (err) => {
      setConfirm(null)
      setActionError(`Failed to delete key: ${errorText(err)}`)
    },
  })

  const keys = keysQuery.data ?? []

  return (
    <section className="rounded-2xl border border-nav-line bg-card p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-ink">API Keys</h2>
        <button
          type="button"
          onClick={() => setShowGenerate(true)}
          className="rounded-full bg-brand px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90"
        >
          + Generate Key
        </button>
      </div>

      {actionError && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-xl border border-error px-3 py-2 text-sm text-error" role="alert">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} className="text-xs hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {keysQuery.isLoading && <LoadingState label="Loading API keys…" />}
      {keysQuery.isError && (
        <ErrorState message={`Failed to load API keys: ${errorText(keysQuery.error)}`} onRetry={() => keysQuery.refetch()} />
      )}
      {keysQuery.isSuccess && keys.length === 0 && <EmptyState message="No API keys yet." />}
      {keys.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                {['Name', 'Key', 'Created', 'Last Used', 'Expires', 'Plan', 'Sites', 'Status', ''].map((h, i) => (
                  <th key={i} className="whitespace-nowrap py-3 pr-4 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className="border-b border-nav-line last:border-0">
                  <td className="py-2.5 pr-4 text-ink">{key.name}</td>
                  <td className="whitespace-nowrap py-2.5 pr-4 font-mono text-xs text-text-secondary">
                    {key.key_prefix}
                    {'•'.repeat(8)}
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-ink">{key.created_at ?? '-'}</td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-ink">{formatLocal(key.last_used_at)}</td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-ink">{key.expires_at ?? 'Never'}</td>
                  <td className="py-2.5 pr-4">
                    <PlanBadge tier={key.subscription_tier} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <SitesBadge mode={key.site_access_mode} count={key.assigned_controller_ids.length} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <StatusBadge record={key} />
                  </td>
                  <td className="whitespace-nowrap py-2.5 text-right">
                    <div className="flex justify-end gap-3 text-xs font-semibold">
                      {key.lifecycle_status === 'active' && (
                        <>
                          <ActionButton onClick={() => setSitesKey(key)}>Sites</ActionButton>
                          <ActionButton onClick={() => setRotateKey(key)} className="text-accent">
                            Rotate
                          </ActionButton>
                        </>
                      )}
                      {(key.lifecycle_status === 'active' || key.lifecycle_status === 'grace_period') && (
                        <ActionButton onClick={() => setConfirm({ kind: 'revoke', key })} className="text-amber-600 dark:text-amber-400">
                          Revoke
                        </ActionButton>
                      )}
                      {['revoked', 'expired', 'grace_expired'].includes(key.lifecycle_status) && (
                        <ActionButton onClick={() => setConfirm({ kind: 'delete', key })} className="text-error">
                          Delete
                        </ActionButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGenerate && (
        <GenerateKeyDialog clientId={clientId} controllers={controllers} onClose={() => setShowGenerate(false)} onCreated={refreshKeys} />
      )}
      {sitesKey && (
        <SitesDialog clientId={clientId} record={sitesKey} controllers={controllers} onClose={() => setSitesKey(null)} onSaved={refreshKeys} />
      )}
      {rotateKey && <RotateDialog clientId={clientId} record={rotateKey} onClose={() => setRotateKey(null)} onRotated={refreshKeys} />}

      <ConfirmModal
        isOpen={confirm !== null}
        title={confirm?.kind === 'delete' ? 'Delete API key' : 'Revoke API key'}
        message={
          confirm?.kind === 'delete'
            ? 'Permanently delete this API key record? The key is already inactive but this removes it from view. This cannot be undone.'
            : 'Revoke this API key? Any integrations using it will immediately lose access.'
        }
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Revoke'}
        isConfirming={revokeMutation.isPending || deleteMutation.isPending}
        onConfirm={() => {
          if (!confirm) return
          if (confirm.kind === 'delete') deleteMutation.mutate(confirm.key.id)
          else revokeMutation.mutate(confirm.key.id)
        }}
        onCancel={() => setConfirm(null)}
      />
    </section>
  )
}

// ---------------------------------------------------------------- helpers

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error'
}

const BADGE = 'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold'

function PlanBadge({ tier }: { tier: SubscriptionTier }) {
  return tier === 'premium' ? (
    <span className={`${BADGE} bg-brand text-white`}>Premium</span>
  ) : (
    <span className={`${BADGE} bg-bg-secondary text-text-secondary`}>Standard</span>
  )
}

function SitesBadge({ mode, count }: { mode: SiteAccessMode; count: number }) {
  return mode === 'selected' ? (
    <span className={`${BADGE} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}>{count} Site(s)</span>
  ) : (
    <span className={`${BADGE} bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300`}>All Sites</span>
  )
}

function StatusBadge({ record }: { record: ApiKeyRecord }) {
  const danger = `${BADGE} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`
  switch (record.lifecycle_status) {
    case 'active':
      return <span className={`${BADGE} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`}>Active</span>
    case 'grace_period':
      return (
        <span
          className={`${BADGE} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}
          title={`Replaced by rotation — valid until ${record.grace_period_ends_at ?? ''}`}
        >
          Grace Period{graceCountdown(record.grace_period_ends_at_iso)}
        </span>
      )
    case 'revoked':
      return <span className={danger}>Revoked</span>
    case 'expired':
      return <span className={danger}>Expired</span>
    case 'grace_expired':
      return <span className={danger}>Grace Ended</span>
    default:
      return <span className={`${BADGE} bg-bg-secondary text-text-secondary`}>Unknown</span>
  }
}

function ActionButton({ onClick, className = 'text-text-primary', children }: { onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={`hover:underline ${className}`}>
      {children}
    </button>
  )
}

const INPUT = 'w-full rounded-xl border border-nav-line bg-input px-3.5 py-2 text-sm text-text-primary focus:border-brand focus:outline-none'
const LABEL = 'mb-1.5 block text-xs font-semibold text-nav-muted'

function Dialog({
  title,
  onClose,
  canClose = true,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  canClose?: boolean
  children: ReactNode
  footer: ReactNode
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canClose) onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, canClose])
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border border-nav-line bg-card p-6 shadow-[0_10px_28px_rgba(16,40,26,0.16),0_1px_3px_rgba(16,40,26,0.08)]"
      >
        <h2 className="mb-4 text-base font-semibold text-text-primary">{title}</h2>
        {children}
        <div className="mt-5 flex justify-end gap-3">{footer}</div>
      </div>
    </div>
  )
}

const BTN_SECONDARY =
  'rounded-full border border-nav-line px-5 py-2 text-sm font-semibold text-text-primary hover:bg-nav-hover disabled:opacity-50'
const BTN_PRIMARY = 'rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'

function SiteAccessFields({
  name,
  mode,
  setMode,
  selected,
  setSelected,
  controllers,
  allLabel,
}: {
  name: string
  mode: SiteAccessMode
  setMode: (m: SiteAccessMode) => void
  selected: string[]
  setSelected: (ids: string[]) => void
  controllers: CompanyController[]
  allLabel: string
}) {
  return (
    <fieldset>
      <legend className={LABEL}>Site Access</legend>
      <label className="flex items-center gap-2 text-sm text-text-primary">
        <input type="radio" name={name} checked={mode === 'all'} onChange={() => setMode('all')} />
        {allLabel}
      </label>
      <label className="mt-1 flex items-center gap-2 text-sm text-text-primary">
        <input type="radio" name={name} checked={mode === 'selected'} onChange={() => setMode('selected')} />
        Selected Sites Only
      </label>
      {mode === 'selected' && (
        <div className="ml-6 mt-2 space-y-1">
          <p className="text-xs text-text-secondary">Select sites this key may access:</p>
          {controllers.length === 0 && <p className="text-xs text-text-secondary">No sites assigned to this client.</p>}
          {controllers.map((c) => (
            <label key={c.controller_id} className="flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={selected.includes(c.controller_id)}
                onChange={(e) =>
                  setSelected(e.target.checked ? [...selected, c.controller_id] : selected.filter((id) => id !== c.controller_id))
                }
              />
              {c.controller_name || c.controller_id}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  )
}

function KeyReveal({ rawKey, warning, children }: { rawKey: string; warning: string; children?: ReactNode }) {
  const [copied, setCopied] = useState(false)
  return (
    <div>
      <div className="mb-3 rounded-xl border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
        <strong>{warning}</strong> It will not be shown again.
      </div>
      <div className="flex gap-2">
        <input readOnly value={rawKey} aria-label="API key" className={`${INPUT} font-mono text-xs`} onFocus={(e) => e.target.select()} />
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(rawKey).then(() => setCopied(true))
          }}
          className={copied ? `${BTN_PRIMARY} px-3` : `${BTN_SECONDARY} px-3`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {children}
    </div>
  )
}

function GenerateKeyDialog({
  clientId,
  controllers,
  onClose,
  onCreated,
}: {
  clientId: string
  controllers: CompanyController[]
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [nameInvalid, setNameInvalid] = useState(false)
  const [tier, setTier] = useState<SubscriptionTier>('standard')
  const [mode, setMode] = useState<SiteAccessMode>('all')
  const [selected, setSelected] = useState<string[]>([])
  const mutation = useMutation({
    mutationFn: () =>
      createApiKey(clientId, {
        name: name.trim(),
        subscription_tier: tier,
        site_access_mode: mode,
        controller_ids: mode === 'selected' ? selected : [],
      }),
    onSuccess: onCreated,
  })
  const created = mutation.data
  return (
    <Dialog
      title="Generate API Key"
      onClose={onClose}
      canClose={!mutation.isPending}
      footer={
        created ? (
          <button type="button" onClick={onClose} className={BTN_PRIMARY}>
            Done
          </button>
        ) : (
          <>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className={BTN_SECONDARY}>
              Cancel
            </button>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => {
                if (!name.trim()) {
                  setNameInvalid(true)
                  return
                }
                setNameInvalid(false)
                mutation.mutate()
              }}
              className={BTN_PRIMARY}
            >
              {mutation.isPending ? 'Generating…' : 'Generate'}
            </button>
          </>
        )
      }
    >
      {created ? (
        <KeyReveal rawKey={created.raw_key} warning="Copy this key now.">
          <p className="mt-2 text-xs text-text-secondary">
            Send it in the <code>x-api-key</code> request header. Store it securely — only the prefix (
            <code>{created.record.key_prefix}</code>•••) will be shown after this.
          </p>
        </KeyReveal>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="new-key-name" className={LABEL}>
              Key label <span className="text-error">*</span>
            </label>
            <input
              id="new-key-name"
              value={name}
              maxLength={120}
              placeholder="e.g. External Dashboard Integration"
              onChange={(e) => {
                setName(e.target.value)
                if (e.target.value.trim()) setNameInvalid(false)
              }}
              className={`${INPUT} ${nameInvalid ? 'border-error' : ''}`}
              aria-invalid={nameInvalid}
              autoFocus
            />
            <p className={`mt-1 text-xs ${nameInvalid ? 'text-error' : 'text-text-secondary'}`}>
              {nameInvalid ? 'A key label is required.' : 'A label to help you identify this key later.'}
            </p>
          </div>
          <div>
            <label htmlFor="new-key-tier" className={LABEL}>
              Subscription Plan
            </label>
            <select id="new-key-tier" value={tier} onChange={(e) => setTier(e.target.value as SubscriptionTier)} className={INPUT}>
              <option value="standard">Standard — daily summary registers only</option>
              <option value="premium">Premium — full dataset</option>
            </select>
            <p className="mt-1 text-xs text-text-secondary">Controls which registers are accessible via this key.</p>
          </div>
          <SiteAccessFields
            name="new-key-site-mode"
            mode={mode}
            setMode={setMode}
            selected={selected}
            setSelected={setSelected}
            controllers={controllers}
            allLabel="Full Client Access — all sites owned by this client"
          />
          {mutation.isError && <p className="text-sm text-error">Failed to generate key: {errorText(mutation.error)}</p>}
        </div>
      )}
    </Dialog>
  )
}

function SitesDialog({
  clientId,
  record,
  controllers,
  onClose,
  onSaved,
}: {
  clientId: string
  record: ApiKeyRecord
  controllers: CompanyController[]
  onClose: () => void
  onSaved: () => void
}) {
  const [mode, setMode] = useState<SiteAccessMode>(record.site_access_mode === 'selected' ? 'selected' : 'all')
  const [selected, setSelected] = useState<string[]>(record.assigned_controller_ids)
  const mutation = useMutation({
    mutationFn: () => updateApiKeySites(clientId, record.id, mode, selected),
    onSuccess: () => {
      onSaved()
      onClose()
    },
  })
  return (
    <Dialog
      title={`Site Access — ${record.name}`}
      onClose={onClose}
      canClose={!mutation.isPending}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={mutation.isPending} className={BTN_SECONDARY}>
            Cancel
          </button>
          <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className={BTN_PRIMARY}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <SiteAccessFields
        name="edit-site-mode"
        mode={mode}
        setMode={setMode}
        selected={selected}
        setSelected={setSelected}
        controllers={controllers}
        allLabel="Full Client Access — all sites"
      />
      {mutation.isError && <p className="mt-3 text-sm text-error">Failed to update sites: {errorText(mutation.error)}</p>}
    </Dialog>
  )
}

const GRACE_OPTIONS = [
  { days: 0, label: 'None — revoke old key immediately' },
  { days: 3, label: '3 days' },
  { days: 7, label: '7 days (recommended)' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
]

function RotateDialog({
  clientId,
  record,
  onClose,
  onRotated,
}: {
  clientId: string
  record: ApiKeyRecord
  onClose: () => void
  onRotated: () => void
}) {
  const [graceDays, setGraceDays] = useState(7)
  const mutation = useMutation({
    mutationFn: () => rotateApiKey(clientId, record.id, graceDays),
    onSuccess: onRotated,
  })
  const result = mutation.data
  return (
    <Dialog
      title={`Rotate — ${record.name}`}
      onClose={onClose}
      canClose={!mutation.isPending}
      footer={
        result ? (
          <button type="button" onClick={onClose} className={BTN_PRIMARY}>
            Done
          </button>
        ) : (
          <>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className={BTN_SECONDARY}>
              Cancel
            </button>
            <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className={BTN_PRIMARY}>
              {mutation.isPending ? 'Rotating…' : 'Rotate Key'}
            </button>
          </>
        )
      }
    >
      {result ? (
        <KeyReveal rawKey={result.raw_key} warning="Copy the new key now.">
          <p className="mt-2 text-xs text-text-secondary">
            {result.grace_period_days > 0
              ? `Old key remains valid until ${result.grace_ends_at}. Delete it after migrating.`
              : 'Old key revoked immediately.'}
          </p>
        </KeyReveal>
      ) : (
        <div>
          <p className="mb-3 text-sm text-text-secondary">
            A new API key will be generated. The current key remains valid during the grace period, giving integrations time to update.
          </p>
          <label htmlFor="rotate-grace-period" className={LABEL}>
            Grace Period
          </label>
          <select id="rotate-grace-period" value={graceDays} onChange={(e) => setGraceDays(Number(e.target.value))} className={INPUT}>
            {GRACE_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-secondary">The old key stops working after this period.</p>
          {mutation.isError && <p className="mt-3 text-sm text-error">Failed to rotate key: {errorText(mutation.error)}</p>}
        </div>
      )}
    </Dialog>
  )
}
